import axios from "axios";

export const backendURL = process.env.REACT_APP_BACKEND_URL

// Simple in-memory cache
const cache = new Map();

const buildCacheKey = (config) => {
  const baseURL = config.baseURL || '';
  const url = config.url || '';
  const params =
    config.params && typeof config.params === 'object'
      ? JSON.stringify(config.params)
      : '';

  return `${baseURL}${url}?${params}`;
};

const instance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor with caching for GET requests
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Only cache public GET requests. Authenticated data like cart/orders
    // should always be fetched fresh.
    const shouldCache =
      config.method === 'get' && !config.headers.Authorization;

    if (shouldCache) {
      const cacheKey = buildCacheKey(config);
      const cachedResponse = cache.get(cacheKey);
      if (cachedResponse) {
        return Promise.reject({
          config,
          response: { data: cachedResponse, status: 200 },
          isCache: true
        });
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with caching
instance.interceptors.response.use(
  (response) => {
    // Cache successful GET requests
    const shouldCache =
      response.config.method === 'get' &&
      !response.config.headers?.Authorization;

    if (shouldCache) {
      const cacheKey = buildCacheKey(response.config);
      cache.set(cacheKey, response.data);
    }

    if (['post', 'patch', 'put', 'delete'].includes(response.config.method)) {
      cache.clear();
    }

    return response;
  },
  async (error) => {
    // Return cached response if available
    if (error.isCache) {
      return error.response;
    }

    // Handle token refresh
    const originalRequest = error.config;
    if ((error.response?.status === 401 || error.response?.data?.message?.includes('invalid token')) 
        && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const response = await instance.post('/api/users/refresh-token');
        const newAccessToken = response.data.refreshedToken;
        localStorage.setItem('accessToken', newAccessToken);
        
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return instance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Add method to clear cache
instance.clearCache = () => {
  cache.clear();
};

export default instance;
